import { z } from 'zod'

import { getHumanReadableSize } from '@buffer-mono/shared-utils/uploads'
import { FileType, FileUploadConfig } from '@buffer-mono/uploader'

export const GUEST_MEDIA_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const

export const GUEST_MEDIA_ACCEPT = GUEST_MEDIA_MIME_TYPES.join(',')
export const GUEST_MEDIA_MAX_COUNT = 10
export const GUEST_MEDIA_TTL_DAYS = 30
export const GUEST_MEDIA_TTL_MS = GUEST_MEDIA_TTL_DAYS * 24 * 60 * 60 * 1000

export type GuestMediaMimeType = (typeof GUEST_MEDIA_MIME_TYPES)[number]

const GUEST_MEDIA_FILE_TYPES: Readonly<Record<GuestMediaMimeType, string>> = {
  'image/jpeg': FileType.JPEG,
  'image/png': FileType.PNG,
  'image/gif': FileType.GIF,
  'image/webp': FileType.WEBP,
}

export const GUEST_MEDIA_UPLOAD_CONFIG = new Map([
  ...Array.from(FileUploadConfig.IMAGE.entries()).filter(
    ([fileType]) => fileType !== FileType.HEIC,
  ),
  ...FileUploadConfig.GIF.entries(),
])

export function getGuestMediaFileType(mimeType: GuestMediaMimeType): string {
  return GUEST_MEDIA_FILE_TYPES[mimeType]
}

export function getGuestMediaMaxFileSizeBytes(
  mimeType: GuestMediaMimeType,
): number {
  return (
    GUEST_MEDIA_UPLOAD_CONFIG.get(GUEST_MEDIA_FILE_TYPES[mimeType])?.maxSize ??
    0
  )
}

export const GUEST_MEDIA_REQUIREMENTS_MESSAGE = `Select a JPEG, PNG, or WebP image up to ${getHumanReadableSize(
  getGuestMediaMaxFileSizeBytes('image/jpeg'),
)}, or a GIF up to ${getHumanReadableSize(
  getGuestMediaMaxFileSizeBytes('image/gif'),
)}.`

export function isGuestMediaMimeType(
  mimeType: string,
): mimeType is GuestMediaMimeType {
  return GUEST_MEDIA_MIME_TYPES.some(
    (allowedMimeType) => allowedMimeType === mimeType,
  )
}

export const GuestMediaSchema = z
  .object({
    id: z.string().refine((id) => id.trim().length > 0),
    assetUrl: z
      .string()
      .url()
      .refine((assetUrl) => {
        const url = new URL(assetUrl)
        return url.protocol === 'https:' && url.search === '' && url.hash === ''
      }),
    fileName: z.string().refine((fileName) => fileName.trim().length > 0),
    mimeType: z.enum(GUEST_MEDIA_MIME_TYPES),
    size: z.number().int().positive(),
    uploadedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().nonnegative(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    altText: z.string().optional(),
  })
  .strict()
  .refine(
    ({ mimeType, size }) => size <= getGuestMediaMaxFileSizeBytes(mimeType),
  )
  .refine(({ expiresAt, uploadedAt }) => uploadedAt < expiresAt)

export type GuestMedia = Readonly<z.infer<typeof GuestMediaSchema>>

export function normalizeGuestMedia(
  entries: readonly unknown[],
): readonly GuestMedia[] {
  const mediaIds = new Set<string>()
  const media: GuestMedia[] = []

  for (const entry of entries) {
    const result = GuestMediaSchema.safeParse(entry)
    if (!result.success || mediaIds.has(result.data.id)) continue

    mediaIds.add(result.data.id)
    media.push(result.data)
    if (media.length === GUEST_MEDIA_MAX_COUNT) break
  }

  return Object.freeze(media)
}

export function isGuestMediaExpired(
  media: GuestMedia,
  currentTime = Date.now(),
): boolean {
  return currentTime >= media.expiresAt
}
